import type { SxProps, Theme } from "@mui/material";

import { isDarkMode } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";
import { theme } from "@/src/theme/theme";
import { homePageHeroColors } from "@/src/theme/tokens";

import { HOMEPAGE_FRONT_PAGE_CONTAINER_STYLES } from "@/src/pages/consumer/HomePage/homePage.constants";

const KATSASTUS_BANNER_CARD_BOX_SHADOW = `0px 0px 40px 0px ${theme.palette.semantic.info.selected}`;
const KATSASTUS_BANNER_DARK_BOX_SHADOW = `0px 0px 40px 0px ${homePageHeroColors.brandGlowDark}`;

/** Extra horizontal inset inside the desktop card (with theme spacing(3) base padding). */
const KATSASTUS_BANNER_DESKTOP_HORIZONTAL_INSET = "1.5rem";

const katsastusBannerDesktopCardPaddingX = `calc(${theme.spacing(
  3
)} + ${KATSASTUS_BANNER_DESKTOP_HORIZONTAL_INSET})`;

export const katsastusBannerSectionSx: SxProps<Theme> = {
  backgroundColor: "background.default",
  ...HOMEPAGE_FRONT_PAGE_CONTAINER_STYLES,
  // The description section above shares the background and carries the gap.
  // Responsive like the shared py, otherwise its media queries win.
  pt: { xs: 0, lg: 0 }
};

export const katsastusBannerCardSx: SxProps<Theme> = schemeSx((muiTheme) => ({
  border: "1px solid",
  borderColor: isDarkMode(muiTheme)
    ? homePageHeroColors.cardDarkBorder
    : theme.palette.semantic.info.dark,
  borderRadius: { xs: 4, md: 10 },
  backgroundColor: muiTheme.palette.background.paper,
  boxShadow: isDarkMode(muiTheme)
    ? KATSASTUS_BANNER_DARK_BOX_SHADOW
    : KATSASTUS_BANNER_CARD_BOX_SHADOW,
  px: {
    xs: 3,
    sm: 3,
    lg: katsastusBannerDesktopCardPaddingX
  },
  py: { xs: 4, md: 3 },
  textAlign: { xs: "center", md: "left" },
  overflow: "hidden",
  gap: { xs: 2, md: 3 }
}));

export const katsastusBannerTextStackSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  width: { xs: "100%", md: "auto" },
  alignItems: { xs: "center", md: "flex-start" },
  gap: 1
};

export const katsastusBannerTitleSx: SxProps<Theme> = schemeSx((muiTheme) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.fontWeightBold,
  fontSize: theme.typography.h3.fontSize,
  lineHeight: 1.3,
  letterSpacing: 0,
  color: isDarkMode(muiTheme)
    ? muiTheme.palette.text.primary
    : theme.palette.brand.katsastus,
  m: 0
}));

export const katsastusBannerSubtitleSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.body1.fontSize,
    lineHeight: 1.5,
    letterSpacing: "0.25px",
    color: isDarkMode(muiTheme)
      ? muiTheme.palette.text.primary
      : theme.palette.brand.katsastus,
    m: 0
  })
);

export const katsastusBannerLogoSx: SxProps<Theme> = {
  width: 61,
  height: 72,
  flexShrink: 0,
  objectFit: "contain",
  display: "block"
};

export const katsastusBannerDesktopLogoWrapperSx: SxProps<Theme> = {
  flexShrink: 0,
  display: { xs: "none", md: "flex" },
  alignItems: "center",
  justifyContent: "center"
};

export const katsastusBannerLogoMobileSx: SxProps<Theme> = {
  width: 102,
  height: 32,
  flexShrink: 0,
  objectFit: "contain",
  display: { xs: "block", md: "none" },
  mt: { xs: 1, md: 0 }
};

export const katsastusBannerCtaButtonSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    minWidth: 200,
    maxWidth: 300,
    height: 48,
    minHeight: 48,
    py: 0,
    borderRadius: "100px",
    borderWidth: 1,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
    ...(isDarkMode(muiTheme) && {
      color: muiTheme.palette.semantic.secondary.main,
      borderColor: muiTheme.palette.semantic.secondary.enabled50p
    })
  })
);
